import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { attendanceService } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
    ClipboardEdit,
    CheckCircle,
    XCircle,
    Clock,
    RefreshCw,
    Loader2,
    Plus,
} from 'lucide-react';

const STATUS_COLORS = {
    pending: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
    approved: 'bg-green-500/10 text-green-600 border-green-500/20',
    rejected: 'bg-red-500/10 text-red-600 border-red-500/20',
};

export default function CorrectionRequestsPage() {
    const { user, isAdmin } = useAuth();
    const canApprove = isAdmin() || user?.role === 'hr' || user?.role === 'manager';

    const [myRequests, setMyRequests] = useState([]);
    const [pendingRequests, setPendingRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);

    // New request form
    const [showNewDialog, setShowNewDialog] = useState(false);
    const [newForm, setNewForm] = useState({
        date: format(new Date(), 'yyyy-MM-dd'),
        requested_check_in: '',
        requested_check_out: '',
        reason: '',
    });
    const [submitLoading, setSubmitLoading] = useState(false);

    // Reject dialog
    const [rejectDialog, setRejectDialog] = useState({ open: false, id: null });
    const [rejectReason, setRejectReason] = useState('');

    useEffect(() => {
        loadData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadData = async () => {
        setLoading(true);
        try {
            const [mine, pending] = await Promise.all([
                attendanceService.getCorrectionRequests(),
                canApprove ? attendanceService.getCorrectionRequests('pending') : Promise.resolve([]),
            ]);
            setMyRequests(mine);
            setPendingRequests(pending);
        } catch (error) {
            toast.error('Failed to load correction requests');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitRequest = async (e) => {
        e.preventDefault();
        if (!newForm.reason.trim()) {
            toast.error('Please provide a reason');
            return;
        }
        setSubmitLoading(true);
        try {
            await attendanceService.createCorrectionRequest(newForm);
            toast.success('Correction request submitted');
            setShowNewDialog(false);
            setNewForm({ date: format(new Date(), 'yyyy-MM-dd'), requested_check_in: '', requested_check_out: '', reason: '' });
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to submit request');
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleApprove = async (id) => {
        setActionLoading(id);
        try {
            await attendanceService.approveCorrectionRequest(id);
            toast.success('Request approved');
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to approve');
        } finally {
            setActionLoading(null);
        }
    };

    const handleReject = async () => {
        if (!rejectReason.trim()) {
            toast.error('Please provide a rejection reason');
            return;
        }
        setActionLoading(rejectDialog.id);
        try {
            await attendanceService.rejectCorrectionRequest(rejectDialog.id, rejectReason);
            toast.success('Request rejected');
            setRejectDialog({ open: false, id: null });
            setRejectReason('');
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to reject');
        } finally {
            setActionLoading(null);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="spinner" />
            </div>
        );
    }

    return (
        <div className="space-y-6 fade-in">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-xl sm:text-3xl font-bold tracking-tight">Attendance Corrections</h1>
                    <p className="text-muted-foreground mt-1">
                        Request corrections for missed or incorrect attendance punches
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={loadData}>
                        <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                    </Button>
                    <Button onClick={() => setShowNewDialog(true)}>
                        <Plus className="w-4 h-4 mr-2" /> New Request
                    </Button>
                </div>
            </div>

            <Tabs defaultValue={canApprove ? 'pending' : 'mine'}>
                <TabsList>
                    {canApprove && (
                        <TabsTrigger value="pending">
                            <Clock className="w-4 h-4 mr-2" />
                            Pending Approval
                            {pendingRequests.length > 0 && (
                                <span className="ml-2 bg-yellow-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                                    {pendingRequests.length}
                                </span>
                            )}
                        </TabsTrigger>
                    )}
                    <TabsTrigger value="mine">
                        <ClipboardEdit className="w-4 h-4 mr-2" /> My Requests
                    </TabsTrigger>
                </TabsList>

                {/* Pending approvals tab (HR/Manager/Admin) */}
                {canApprove && (
                    <TabsContent value="pending">
                        <Card>
                            <CardHeader>
                                <CardTitle>Pending Correction Requests</CardTitle>
                                <CardDescription>Review and act on employee attendance correction requests</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Employee</TableHead>
                                            <TableHead>Date</TableHead>
                                            <TableHead>Requested Times</TableHead>
                                            <TableHead>Reason</TableHead>
                                            <TableHead className="text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {pendingRequests.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                                                    No pending requests
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            pendingRequests.map((req) => (
                                                <TableRow key={req.id}>
                                                    <TableCell className="font-medium">{req.employee_name || req.employee_id}</TableCell>
                                                    <TableCell>{req.date}</TableCell>
                                                    <TableCell className="font-mono text-sm">
                                                        {req.requested_check_in || '—'} → {req.requested_check_out || '—'}
                                                    </TableCell>
                                                    <TableCell className="max-w-xs truncate">{req.reason}</TableCell>
                                                    <TableCell className="text-right">
                                                        <div className="flex justify-end gap-2">
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                className="text-green-600 border-green-600/30 hover:bg-green-50"
                                                                onClick={() => handleApprove(req.id)}
                                                                disabled={actionLoading === req.id}
                                                            >
                                                                {actionLoading === req.id
                                                                    ? <Loader2 className="w-3 h-3 animate-spin" />
                                                                    : <CheckCircle className="w-3 h-3 mr-1" />}
                                                                Approve
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                className="text-red-600 border-red-600/30 hover:bg-red-50"
                                                                onClick={() => setRejectDialog({ open: true, id: req.id })}
                                                                disabled={actionLoading === req.id}
                                                            >
                                                                <XCircle className="w-3 h-3 mr-1" /> Reject
                                                            </Button>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                )}

                {/* My requests tab */}
                <TabsContent value="mine">
                    <Card>
                        <CardHeader>
                            <CardTitle>My Correction Requests</CardTitle>
                            <CardDescription>Track the status of your submitted requests</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Date</TableHead>
                                        <TableHead>Requested Times</TableHead>
                                        <TableHead>Reason</TableHead>
                                        <TableHead>Status</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {myRequests.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                                                No correction requests yet
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        myRequests.map((req) => (
                                            <TableRow key={req.id}>
                                                <TableCell>{req.date}</TableCell>
                                                <TableCell className="font-mono text-sm">
                                                    {req.requested_check_in || '—'} → {req.requested_check_out || '—'}
                                                </TableCell>
                                                <TableCell className="max-w-xs truncate">{req.reason}</TableCell>
                                                <TableCell>
                                                    <span className={`text-xs px-2 py-1 rounded-full border font-medium ${STATUS_COLORS[req.status] || ''}`}>
                                                        {req.status}
                                                    </span>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* New Request Dialog */}
            <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
                <DialogContent className="max-w-lg max-h-[90dvh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>New Attendance Correction Request</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmitRequest} className="space-y-4 py-2">
                        <div className="space-y-2">
                            <Label>Date *</Label>
                            <Input
                                type="date"
                                value={newForm.date}
                                max={format(new Date(), 'yyyy-MM-dd')}
                                onChange={(e) => setNewForm({ ...newForm, date: e.target.value })}
                                required
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Requested Check-in</Label>
                                <Input
                                    type="time"
                                    value={newForm.requested_check_in}
                                    onChange={(e) => setNewForm({ ...newForm, requested_check_in: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Requested Check-out</Label>
                                <Input
                                    type="time"
                                    value={newForm.requested_check_out}
                                    onChange={(e) => setNewForm({ ...newForm, requested_check_out: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Reason *</Label>
                            <Textarea
                                placeholder="Explain why you need this correction..."
                                value={newForm.reason}
                                onChange={(e) => setNewForm({ ...newForm, reason: e.target.value })}
                                rows={3}
                                required
                            />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setShowNewDialog(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={submitLoading}>
                                {submitLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                Submit Request
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Reject Dialog */}
            <Dialog open={rejectDialog.open} onOpenChange={(open) => setRejectDialog({ open, id: null })}>
                <DialogContent className="max-w-lg max-h-[90dvh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Reject Correction Request</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-2 py-2">
                        <Label>Reason for rejection *</Label>
                        <Textarea
                            placeholder="Provide a reason for rejection..."
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            rows={3}
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRejectDialog({ open: false, id: null })}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={handleReject} disabled={!!actionLoading}>
                            {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Reject
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
